// Cave84 — Privacy store
// Global switch that hides every currency value in the app at once.
// Default is ALWAYS hidden — other people can see the screen, only the owner
// actively reveals numbers, and only for the current tab session.
//
// Persistence: sessionStorage (not localStorage) so a revealed state never
// survives closing the tab. A fresh session / full reload always starts
// hidden, per spec.

import { create } from 'zustand';

const STORAGE_KEY = 'cave84.privacy.revealed';

function readInitialHidden(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    // Presence of the key (set to '1') means the user revealed values
    // earlier in this tab session. Absence (or any other value) => hidden.
    return window.sessionStorage.getItem(STORAGE_KEY) !== '1';
  } catch {
    // sessionStorage can throw in locked-down contexts (e.g. private mode
    // with storage disabled) — fail safe to hidden.
    return true;
  }
}

function persist(hidden: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (hidden) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    }
  } catch {
    // Ignore — worst case the revealed state doesn't persist across
    // navigation, it still works for the current render.
  }
}

interface PrivacyState {
  hidden: boolean;
  reveal: () => void;
  conceal: () => void;
  toggle: () => void;
}

export const usePrivacyStore = create<PrivacyState>((set) => ({
  hidden: readInitialHidden(),
  reveal: () => set(() => { persist(false); return { hidden: false }; }),
  conceal: () => set(() => { persist(true); return { hidden: true }; }),
  toggle: () => set((s) => { const next = !s.hidden; persist(next); return { hidden: next }; }),
}));
